import { AuctionInfo, ListingInfo } from '@apeiron/common'
import { CurrencyTokenType, useCurrencyContext } from '@apeiron/currency'
import { MayNull } from '@apeiron/temp'
import { Box, styled } from '@mui/material'
import DateLinearProgress from '@src/components/share/generic/DateLinearProgress'
import PriceText from '@src/components/share/generic/PriceText'
import * as R from 'ramda'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const Text = styled(Box)`
  color: #ffffff;
`

const StyledPriceText = styled(PriceText)`
  .Custom-Icon {
    ${props => props.theme.breakpoints.up('res480')} {
      width: 15px;
      height: 15px;
    }
    ${props => props.theme.breakpoints.down('res480')} {
      width: 14px;
      height: 14px;
    }
  }
  .Custom-Text {
    ${props => props.theme.breakpoints.up('res480')} {
      font-size: 16px;
      line-height: 18px;
    }
    ${props => props.theme.breakpoints.down('res480')} {
      font-size: 14px;
      line-height: 16px;
    }
  }
`

const Container = styled(Box)`
  width: 158px;
  ${props => props.theme.breakpoints.up('res480')} {
    width: 197px;
  }
`

const PriceTextContainer = styled(Box)`
  display: flex;
  margin: 1px 6px 8px 6px;
  justify-content: space-between;
`

const DayText = styled(Text)`
  font-size: 12px;
  line-height: 14px;
  text-align: center;
`

const ApeironPriceDuration: FC<Props> = props => {
  const { className, listingInfo, showDay = true } = props

  const { getCurrency, getApeironPrice } = useCurrencyContext()

  const currency = getCurrency(
    listingInfo?.currencyType || CurrencyTokenType.Unspecified
  )

  const info: MayNull<AuctionInfo> = useMemo(() => {
    return R.isNotNil(listingInfo) && listingInfo.duration > 0
      ? {
          now: new Date(),
          startAt: new Date(listingInfo.startDate),
          endAt: new Date(
            new Date(listingInfo.startDate).getTime() +
              listingInfo.duration * 1000
          ),
          startPrice: listingInfo.startPrice,
          endPrice: listingInfo.endPrice
        }
      : null
  }, [listingInfo])

  const hasAuctionInfo = R.isNotNil(info)

  const { t } = useTranslation()

  const durationTimeFromStartToNow = hasAuctionInfo
    ? info.endAt.getTime() - info.startAt.getTime()
    : 0

  const durationDayFromStartToNow = Math.floor(
    durationTimeFromStartToNow / (1000 * 3600 * 24)
  )

  const formattedStartPrice = info ? getApeironPrice(null, info.startPrice) : ''

  const formattedEndPrice = info ? getApeironPrice(null, info.endPrice) : ''

  return hasAuctionInfo ? (
    <Container className={className}>
      {showDay && info.now < info.endAt && (
        <DayText>
          {t('trade.price.duration_of_day', {
            count: durationDayFromStartToNow
          })}
        </DayText>
      )}
      <PriceTextContainer className={'Custom-Price-Container'}>
        <StyledPriceText
          className={'Custom-Price'}
          icon={currency?.imageLite}
          displayPrice={formattedStartPrice}
        />
        <StyledPriceText
          className={'Custom-Price'}
          icon={currency?.imageLite}
          displayPrice={formattedEndPrice}
        />
      </PriceTextContainer>
      <DateLinearProgress
        startAt={info.startAt}
        endAt={info.endAt}
        now={info.now}
      />
    </Container>
  ) : (
    <></>
  )
}

type Props = {
  className?: string
  listingInfo: MayNull<ListingInfo>
  showDay?: boolean
}

export default ApeironPriceDuration
